import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { apiErrorMessage } from '@/utils/apiError'
import { navigateTo, currentPath } from '@/lib/navigation'

// A page can fire several AI-backed queries at once; without this guard each
// 428 would queue its own toast and its own redirect.
let aiKeyRedirectPending = false

// Detect the backend's ai_key_required 428 response, surface a toast, and
// send the user to Settings → AI. Returns true when handled so the caller
// can short-circuit its generic error path. See AiKeyRequiredException on
// the backend.
function handleAiKeyRequired(err) {
  const res = err?.response
  if (res?.status !== 428) return false
  const data = res?.data
  if (!data || data.code !== 'ai_key_required') return false
  if (aiKeyRedirectPending) return true
  const provider = (data.provider || 'AI').toString()
  const capability = data.capability || 'ai'
  const target = data.settings_url || '/ai-settings'
  if (currentPath() === target) return true
  aiKeyRedirectPending = true
  // A plain string, not a render callback — this file has no JSX
  // transform, and react-hot-toast accepts either.
  toast.error(
    `Set up your ${provider} API key to use ${capability}. Opening Settings…`,
    { duration: 4500 }
  )
  // Give the toast a moment to register before moving. A client-side
  // navigation, so an in-progress note or transcript edit elsewhere in the
  // tree isn't thrown away by a reload.
  setTimeout(() => {
    aiKeyRedirectPending = false
    if (currentPath() !== target) navigateTo(target)
  }, 800)
  return true
}

// Silence toasts for specific handled cases so the global handler doesn't
// double-fire when a caller already surfaces its own error UI. The
// per-call opt-out is React Query's `meta.suppressErrorToast`, checked by
// the cache handlers below.
const isSilenced = (err) => {
  // 401 is handled by the axios interceptor (redirect to /login);
  // toasting on top of that would be noise.
  return err?.response?.status === 401
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (query?.meta?.suppressErrorToast || isSilenced(err)) return
      if (handleAiKeyRequired(err)) return
      toast.error(apiErrorMessage(err, 'Failed to load data.'))
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation?.meta?.suppressErrorToast || isSilenced(err)) return
      if (handleAiKeyRequired(err)) return

      // Only toast for mutations that do NOT handle the error themselves.
      //
      // This used to fire unconditionally, on the theory that callers
      // rendering their own error UI would opt out with
      // meta.suppressErrorToast. Nothing ever did: 91 mutations across 35
      // files define their own onError toast and not one sets the flag, so
      // every failed mutation in the app raised two identical toasts. QA
      // filed it against Add Team Member ("Your current plan allows only 1
      // team member" twice), but it was every mutation, everywhere.
      //
      // Opting in by default is the wrong way round — a caller that has
      // written a specific message has already decided what the user should
      // see. This handler is the fallback for the ones that haven't.
      // A caller that wants BOTH its own handling and the generic toast can
      // still call toast.error itself.
      if (typeof mutation?.options?.onError === 'function') return

      toast.error(apiErrorMessage(err, 'Something went wrong. Please try again.'))
    },
  }),
})
